import React, { useState } from 'react'
import { DisclosureAgreement } from '../../types'
import CommitmentVerifyModal from './CommitmentVerifyModal'

interface Props {
  agreement: DisclosureAgreement
}

const DisclosureCommitmentBadge: React.FC<Props> = ({ agreement }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const chainLength = agreement.level_commitments?.length ?? 0

  if (chainLength === 0) return null

  // A non-empty level_commitments array is a tamper-evident hash chain — that is the
  // baseline integrity guarantee and is ALWAYS present once the agreement advances.
  // Optional Ed25519 device-keypair signatures add non-repudiation on top; they are
  // only present when both orgs have set up a signing device. Treat the plain
  // hash-chain state as a normal "Committed" (neutral) — not a warning — and reserve
  // the green "(signed)" badge for when device signatures are present.
  const allSigned =
    chainLength > 0 &&
    (agreement.level_commitments?.every(c => c.signed === true) ?? false)

  return (
    <>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer ${
          allSigned
            ? 'bg-green-100 text-green-800 hover:bg-green-200'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
        onClick={() => setModalOpen(true)}
        title={
          allSigned
            ? 'Commitment chain is device-signed (non-repudiable). Click to verify.'
            : 'Tamper-evident commitment chain recorded. Click to verify.'
        }
      >
        {allSigned ? '✓ Committed (signed)' : '✓ Committed'}
      </span>
      {modalOpen && (
        <CommitmentVerifyModal
          agreementId={agreement.agreement_id}
          onClose={() => setModalOpen(false)}
          orgId={agreement.my_org_id}
        />
      )}
    </>
  )
}

export default DisclosureCommitmentBadge
